import { ChoiceGroup } from "@/components/forms/ChoiceGroup";
import { cn } from "@/utils/cn";
import { useWireAnswers } from "./wireAnswersContext";
import type { ChWireQuestion } from "./caseHistoryWireTypes";
import {
  getWireAnswerKey,
  normalizeChoices,
  wireChoicesRaw,
  wireElementType,
  wireHtml,
  wireName,
  wireTitle,
} from "./caseHistoryWireTypes";

/** 16px text so iOS Safari does not zoom on focus; 44px tall; navy focus ring (DESIGN.md). */
const inputClass =
  "block min-h-11 w-full rounded-lg border border-[#e2e2ec] bg-white px-3 py-2 text-base text-[#1E252B] placeholder:text-[#6F7375] focus:border-[#2A4064] focus:outline-none focus:ring-2 focus:ring-[#2A4064]/30";

function fieldName(name: string) {
  return `chWireQ_${name}`.replace(/[^a-zA-Z0-9_-]/g, "_");
}

function wireRawNum(el: ChWireQuestion, camel: keyof ChWireQuestion, pascal: string, fallback: number): number {
  const r = el as unknown as Record<string, unknown>;
  const v = el[camel] ?? r[pascal];
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function CaseHistoryWireQuestion({ question: q }: { question: ChWireQuestion }) {
  const { answers, setAnswer } = useWireAnswers();
  const key = getWireAnswerKey(q);
  const wt = wireElementType(q);
  const choiceOpts = normalizeChoices(wireChoicesRaw(q));
  const rateMin = wireRawNum(q, "rateMin", "RateMin", 1);
  const rateMax = wireRawNum(q, "rateMax", "RateMax", 5);
  const r = q as unknown as Record<string, unknown>;
  const inputTypeRaw = q.inputType ?? r.InputType;
  const required = Boolean(q.isRequired ?? r.IsRequired);
  const textInputType =
    inputTypeRaw === "number" || inputTypeRaw === "date" ? inputTypeRaw : "text";

  const strVal = answers[key] == null ? "" : String(answers[key]);
  const boolStr =
    answers[key] === true ? "yes" : answers[key] === false ? "no" : "";
  const arrayVal: string[] = Array.isArray(answers[key])
    ? (answers[key] as unknown[]).map(String)
    : [];
  const ratingVal = (() => {
    const v = answers[key];
    const n = typeof v === "number" && !Number.isNaN(v) ? v : rateMin;
    return Math.min(rateMax, Math.max(rateMin, n));
  })();

  const displayTitle = wireTitle(q);
  const nm = wireName(q);
  const idBase = fieldName(key).replace(/[^a-zA-Z0-9_-]/g, "-");

  if (wt === "integer" || wt === "number") {
    const numVal = answers[key] == null ? "" : String(answers[key]);
    return (
      <div className="mb-4">
        <label htmlFor={`int-${idBase}`} className="choice-textarea-label">
          {displayTitle || nm}
          {required ? <span aria-hidden> *</span> : null}
        </label>
        <input
          id={`int-${idBase}`}
          name={fieldName(key)}
          type="number"
          inputMode="numeric"
          step={wt === "integer" ? 1 : "any"}
          className={cn(inputClass, "max-w-xs")}
          placeholder={displayTitle || nm}
          value={numVal}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") { setAnswer(key, ""); return; }
            const n = wt === "integer" ? parseInt(raw, 10) : parseFloat(raw);
            setAnswer(key, Number.isFinite(n) ? n : raw);
          }}
        />
      </div>
    );
  }

  if (wt === "text" || wt === "comment") {
    return (
      <div className="mb-4">
        <label htmlFor={`text-${idBase}`} className="choice-textarea-label">
          {displayTitle || nm}
          {required ? <span aria-hidden> *</span> : null}
        </label>
        {wt === "text" ? (
          <input
            id={`text-${idBase}`}
            name={fieldName(key)}
            type={textInputType}
            className={inputClass}
            placeholder={displayTitle || nm}
            value={strVal}
            onChange={(e) => setAnswer(key, e.target.value)}
          />
        ) : (
          <textarea
            id={`text-${idBase}`}
            name={fieldName(key)}
            rows={3}
            className={inputClass}
            placeholder={displayTitle || nm}
            value={strVal}
            onChange={(e) => setAnswer(key, e.target.value)}
          />
        )}
      </div>
    );
  }

  // The question is the fieldset legend; choices are 44px pills (DESIGN.md). Nothing is selected
  // until the patient taps, and answers keep their shape: boolean, choice value, or value list.
  const legend = (
    <>
      {displayTitle || nm}
      {required ? <span aria-hidden> *</span> : null}
    </>
  );

  if (wt === "boolean") {
    return (
      <ChoiceGroup
        id={`bool-${idBase}`}
        legend={legend}
        required={required}
        layout="segmented"
        options={[
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ]}
        value={boolStr || null}
        onChange={(v) => {
          if (v) setAnswer(key, v === "yes");
        }}
      />
    );
  }

  if (wt === "radiogroup") {
    return (
      <ChoiceGroup
        id={`radio-${idBase}`}
        legend={legend}
        required={required}
        options={choiceOpts.map((opt) => ({ value: opt.value, label: opt.text }))}
        value={strVal || null}
        onChange={(v) => {
          if (v) setAnswer(key, v);
        }}
      />
    );
  }

  if (wt === "checkbox") {
    return (
      <ChoiceGroup
        id={`check-${idBase}`}
        multiple
        legend={legend}
        required={required}
        options={choiceOpts.map((opt) => ({ value: opt.value, label: opt.text }))}
        value={arrayVal}
        onChange={(next) => setAnswer(key, next)}
      />
    );
  }

  if (wt === "dropdown") {
    return (
      <div className="mb-4">
        <label htmlFor={`dd-${idBase}`} className="choice-textarea-label">
          {displayTitle || nm}
          {required ? <span aria-hidden> *</span> : null}
        </label>
        <select
          id={`dd-${idBase}`}
          name={fieldName(key)}
          className={cn(inputClass, "max-w-md")}
          value={strVal}
          onChange={(e) => setAnswer(key, e.target.value)}
        >
          <option value="">Select…</option>
          {choiceOpts.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.text}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (wt === "rating") {
    const display =
      typeof answers[key] === "number" && !Number.isNaN(Number(answers[key]))
        ? String(answers[key])
        : `(${rateMin}–${rateMax})`;
    return (
      <div className="mb-4">
        <label htmlFor={`rating-${idBase}`} className="choice-textarea-label">
          {displayTitle || nm}
          {required ? <span aria-hidden> *</span> : null}
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <input
            id={`rating-${idBase}`}
            type="range"
            min={rateMin}
            max={rateMax}
            className="h-2 w-full max-w-xs cursor-pointer rounded-lg accent-[#2A4064]"
            value={ratingVal}
            onChange={(e) => setAnswer(key, Number(e.target.value))}
          />
          <span className="text-[15px] tabular-nums text-[#1E252B]">{display}</span>
        </div>
      </div>
    );
  }

  if (wt === "html") {
    return (
      <div
        className="prose prose-sm mb-4 max-w-none text-gray-800"
        dangerouslySetInnerHTML={{ __html: wireHtml(q) }}
      />
    );
  }

  return (
    <p className="mb-4 text-sm text-amber-800">
      Unsupported field type “{wt || q.type}” ({nm}).
    </p>
  );
}
